import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { ArrowRight, CheckCircle, EnvelopeSimple } from "@phosphor-icons/react";
import SEO from "../components/SEO";

const CATEGORIES = [
  { value: "general", label: "General" },
  { value: "story", label: "Story" },
  { value: "podcast_guest", label: "Podcast Guest" },
  { value: "partnership", label: "Partnership / Press" },
];

const inputCls =
  "w-full border border-white/15 bg-black px-4 py-3 text-base text-white placeholder-white/40 outline-none focus:border-[#D4FF00]";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    category: "general",
    subject: "",
    message: "",
    website: "", // honeypot — never shown
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", category: "general", subject: "", message: "", website: "" });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Couldn't send message — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#050505]">
      <SEO
        title="Contact"
        description="Get in touch with Baseline Breakdown — story tips, podcast guests, partnerships, and feedback."
      />
      {/* Header */}
      <section className="border-b border-white/10">
        <div className="mx-auto max-w-[1400px] px-4 py-16 sm:px-6 sm:py-20 lg:px-12 lg:py-28">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4FF00] sm:text-xs">
            <EnvelopeSimple size={14} weight="bold" /> Contact
          </div>
          <h1 className="mt-4 max-w-4xl font-display text-4xl font-bold leading-[0.95] tracking-tighter text-white sm:text-5xl md:text-6xl lg:text-7xl">
            Contact us
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
            Fill out the form below and we will get back to you as soon as possible!
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16 lg:px-12 lg:py-20">
        {sent ? (
          <div data-testid="contact-success" className="border border-[#D4FF00] bg-[#D4FF00]/5 p-8 text-center sm:p-12">
            <CheckCircle size={48} weight="bold" className="mx-auto text-[#D4FF00]" />
            <h2 className="mt-6 font-display text-3xl font-bold tracking-tighter text-white sm:text-4xl">
              Message received.
            </h2>
            <p className="mt-3 text-sm text-white/70">
              Thanks for reaching out. We'll be in touch soon.
            </p>
            <button
              data-testid="contact-send-another"
              onClick={() => setSent(false)}
              className="mt-8 border border-white/20 px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] text-white hover:border-[#D4FF00] hover:text-[#D4FF00]"
            >
              Send another
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
            {/* Honeypot — hidden from users, visible to bots */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={(e) => setField("website", e.target.value)}
              className="absolute left-[-9999px] h-0 w-0 opacity-0"
              aria-hidden="true"
            />

            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.2em] text-white/60">
                  Name
                </label>
                <input
                  data-testid="contact-name"
                  type="text"
                  required
                  maxLength={120}
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  className={`mt-2 ${inputCls}`}
                  placeholder="Jane Sampras"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.2em] text-white/60">
                  Email
                </label>
                <input
                  data-testid="contact-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  className={`mt-2 ${inputCls}`}
                  placeholder="jane@inbox.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.2em] text-white/60">
                What's it about?
              </label>
              <select
                data-testid="contact-category"
                value={form.category}
                onChange={(e) => setField("category", e.target.value)}
                className={`mt-2 ${inputCls}`}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value} className="bg-black">
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.2em] text-white/60">
                Subject
              </label>
              <input
                data-testid="contact-subject"
                type="text"
                required
                maxLength={160}
                value={form.subject}
                onChange={(e) => setField("subject", e.target.value)}
                className={`mt-2 ${inputCls}`}
                placeholder="Quick line about your message"
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.2em] text-white/60">
                Message
              </label>
              <textarea
                data-testid="contact-message"
                required
                rows={8}
                maxLength={5000}
                value={form.message}
                onChange={(e) => setField("message", e.target.value)}
                className={`mt-2 ${inputCls} resize-y`}
                placeholder="Tell us what's on your mind…"
              />
              <div className="mt-1 text-right text-[10px] uppercase tracking-[0.2em] text-white/40">
                {form.message.length} / 5000
              </div>
            </div>

            {error && (
              <div data-testid="contact-error" className="border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                {error}
              </div>
            )}

            <button
              data-testid="contact-submit"
              type="submit"
              disabled={submitting}
              className="flex w-full items-center justify-center gap-2 border-2 border-[#D4FF00] bg-[#D4FF00] px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] text-black transition-transform hover:-translate-y-0.5 disabled:opacity-50 sm:w-auto"
            >
              {submitting ? "Sending…" : <>Send message <ArrowRight size={14} weight="bold" /></>}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
